import React, { useState, createRef, useEffect, useRef } from "react";
import "./externalShareOTP.scss";
import Button from "components/Button/index";
import { useToast } from "components/Toaster/index";
import useScreenMobile from "hooks/useScreen";

const ExternalShareOTP = ({
  onOTPVerified,
  useExistingAuth = false // Option to use existing TOTP system
}) => {
  const isMobile = useScreenMobile({ size: 992 });
  const count = 6;
  const [otp, setOtp] = useState(Array(count).fill(""));
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Please enter a complete 6-digit code");
  const [loading, setLoading] = useState(false);
  const showToast = useToast();

  const refs = useRef([]);
  if (refs.current.length !== count) {
    refs.current = Array(count).fill(0).map((_, i) => refs.current[i] || createRef());
  }

  const isDigit = (k) => /^[0-9]$/.test(k);

  // Handle digit input change
  const handleChange = (val, idx) => {
    if (!/^\d{0,2}$/.test(val)) return; // Allow 0 to 2 digits only

    const lastDigit = val.slice(-1); // Always take the last digit
    const newOtp = [...otp];
    newOtp[idx] = lastDigit;
    setOtp(newOtp);

    if (showError) {
      setShowError(false);
      setErrorMessage("Please enter a complete 6-digit code");
    }

    if (lastDigit && idx < count - 1 && refs.current[idx + 1]?.current) {
      refs.current[idx + 1].current.focus();
    } else if (lastDigit && idx === count - 1) {
      refs.current[idx].current?.blur();
    }
  };

  // Handle paste events
  const handlePaste = (e, idx) => {
    e.preventDefault();
    const pastedData = (e.clipboardData || window.clipboardData).getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, count); // Extract only digits, max 6

    if (!digits) return;

    const newOtp = [...otp];
    
    // If user pastes a full 6-digit code, start filling from the first box
    // otherwise start from the focused box
    const startIdx = digits.length === count ? 0 : idx;

    // Fill OTP fields
    for (let i = 0; i < digits.length && (startIdx + i) < count; i++) {
      newOtp[startIdx + i] = digits[i];
    }

    setOtp(newOtp);

    // Clear error state
    if (showError) {
      setShowError(false);
      setErrorMessage("Please enter a complete 6-digit code");
    }

    // Focus management
    const nextEmptyIdx = newOtp.findIndex((digit, i) => i >= startIdx && digit === "");
    const focusIdx = nextEmptyIdx !== -1
      ? nextEmptyIdx
      : Math.min(startIdx + digits.length, count - 1);

    if (focusIdx >= 0 && focusIdx < count && refs.current[focusIdx]?.current) {
      setTimeout(() => {
        if (refs.current[focusIdx]?.current) {
          try {
            refs.current[focusIdx].current.focus();
          } catch (error) {
            console.warn("Failed to focus OTP input:", error);
          }
        }
      }, 0);
    }
  };

  // Handle keydown events for backspace and arrow keys
  const handleKeyDown = (e, idx) => {
    if (!refs.current[idx]?.current) return;

    if (isDigit(e.key)) {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[idx] = e.key;
      setOtp(newOtp);

      if (idx < count - 1 && refs.current[idx + 1]?.current) {
        refs.current[idx + 1].current.focus();
      }

      if (showError) {
        setShowError(false);
        setErrorMessage("Please enter a complete 6-digit code");
      }
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      
      if (otp[idx]) {
        // If there's a digit, delete it
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0 && refs.current[idx - 1]?.current) {
        // If field is empty, move focus to previous and delete its digit
        newOtp[idx - 1] = "";
        setOtp(newOtp);
        refs.current[idx - 1].current.focus();
        setTimeout(() => {
          if (refs.current[idx - 1]?.current) {
            refs.current[idx - 1].current.setSelectionRange(1, 1);
          }
        }, 0);
      }
    }

    if (e.key === "ArrowLeft") {
      if (idx > 0 && refs.current[idx - 1]?.current) {
        refs.current[idx - 1].current.focus();
        setTimeout(() => {
          if (refs.current[idx - 1]?.current) {
            refs.current[idx - 1].current.setSelectionRange(1, 1);
          }
        }, 0);
      }
      e.preventDefault();
    }

    if (e.key === "ArrowRight") {
      if (idx < count - 1 && refs.current[idx + 1]?.current) {
        refs.current[idx + 1].current.focus();
        setTimeout(() => {
          if (refs.current[idx + 1]?.current) {
            refs.current[idx + 1].current.setSelectionRange(1, 1);
          }
        }, 0);
      }
      e.preventDefault();
    }

    if (e.key === "Enter") {
      const isOtpComplete = otp.every((digit) => digit !== "");
      if (isOtpComplete) {
        handleVerifyOTP();
      }
    }
  };

  // Handle click event for focusing input
  const handleClick = (idx) => {
    if (refs.current[idx]?.current) {
      refs.current[idx].current.focus();
    }
  };

  useEffect(() => {
    if (refs.current[0]?.current) {
      refs.current[0].current.focus();
    }
  }, []);

  // Handle OTP verification
  const handleVerifyOTP = async () => {
    const otpValue = otp.join("");
    try {
      setLoading(true);
      setShowError(false);

      if (otpValue.length === count) {
        await onOTPVerified(otpValue);
        showToast("OTP verified successfully!", "success");
      } else {
        setErrorMessage("Please enter a complete 6-digit code");
        setShowError(true);
        showToast("Please enter a complete 6-digit code", "error");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      const errMsg = "Invalid OTP";
      setErrorMessage(errMsg);
      setShowError(true);
      showToast(errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="externalShareVerify">
      <div className="container externalShareContainer">
        <div className="externalShareBox">
          <div className="externalShareCommonHeading">
            <h1 className="externalShareBrandName">YSafe</h1>
            <h2 className="externalShareSubHeadline">World&apos;s Safest Way to Share</h2>
            <p>
              {useExistingAuth
                ? "Please enter 6-digit code to access the shared content"
                : "Please enter 6-digit OTP to access the shared content"
              }
            </p>
          </div>

          <div className="externalShareCodeBox">
            <div className="externalShareOtpContainer">
              <div className="externalShareInputsWrapper" id="externalShareOtp">
                {otp.map((digit, idx) => (
                  <input
                    ref={refs.current[idx]}
                    key={`input-${idx + 1}`}
                    id={`input-${idx + 1}`}
                    name={`input-${idx + 1}`}
                    value={digit}
                    style={{
                      border: showError ? "1px solid var(--accent-colors-red)" : "1px solid rgba(255, 255, 255, 0.15)",
                      width: isMobile ? "13%" : "56px",
                      height: isMobile ? "40px" : "56px",
                      maxWidth: "56px",
                      minWidth: "30px"
                    }}
                    maxLength={1}
                    onChange={(e) => handleChange(e.target.value, idx)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    onPaste={(e) => handlePaste(e, idx)}
                    onClick={() => handleClick(idx)}
                    onFocus={(e) => {
                      const input = e.target;
                      input.setSelectionRange(input.value.length, input.value.length);
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    type="tel"
                    placeholder=""
                  />
                ))}
              </div>

              <span
                style={{ visibility: showError ? "visible" : "hidden" }}
                className="externalShareErrorText"
              >
                {errorMessage}
              </span>
            </div>

            <Button
              loading={loading}
              title={useExistingAuth ? "Verify TOTP" : "Verify OTP"}
              type="violet"
              onClick={() => handleVerifyOTP()}
              disabled={otp.some((digit) => digit === "")}
            />

            {useExistingAuth && (
              <div className="externalShareOtpInfo">
                <p className="note">Enter any 6-digit code to proceed</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExternalShareOTP;
